"use client"

import { ArbreResponse } from '@repo/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { services } from '../../../lib/api/services';

export interface ArbreImageUploadParams {
  arbreId: number;
  field: 'img1' | 'img2' | 'img3';
  file: File;
}

export interface ArbreImageDeleteParams {
  arbreId: number;
  field: 'img1' | 'img2' | 'img3';
}

export interface ArbreImageRotateParams {
  arbreId: number;
  field: 'img1' | 'img2' | 'img3';
  rotation: number;
}

/**
 * Hook for uploading/updating arbre images
 * Returns mutation object with upload functionality
 */
export function useArbreImageUpload() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ arbreId, field, file }: ArbreImageUploadParams): Promise<ArbreResponse> => {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error('Type de fichier non supporté. Utilisez JPEG, PNG ou WebP.');
      }

      // Validate file size (5MB max)
      const maxSize = 20 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        throw new Error('Le fichier est trop volumineux. Taille maximale: 5MB.');
      }

      return services.arbre.updateImage(arbreId, field, file);
    },
    onSuccess: (data, { arbreId }) => {
      // Invalidate and refetch relevant queries
      queryClient.invalidateQueries({ queryKey: ['arbres'] });
      queryClient.invalidateQueries({ queryKey: ['arbre', arbreId] });
      queryClient.invalidateQueries({ queryKey: ['inventaire'] });

      // Optionally update the cache directly with the new data
      queryClient.setQueryData(['arbre', arbreId], data);
    },
    onError: (error) => {
      console.error('Error uploading arbre image:', error);
    }
  });
}

/**
 * Hook for deleting arbre images
 * Returns mutation object with delete functionality
 */
export function useArbreImageDelete() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ arbreId, field }: ArbreImageDeleteParams): Promise<ArbreResponse> => {
      return services.arbre.deleteImage(arbreId, field);
    },
    onSuccess: (data, { arbreId }) => {
      // Invalidate and refetch relevant queries
      queryClient.invalidateQueries({ queryKey: ['arbres'] });
      queryClient.invalidateQueries({ queryKey: ['arbre', arbreId] });
      queryClient.invalidateQueries({ queryKey: ['inventaire'] });

      // Update the cache directly with the new data
      queryClient.setQueryData(['arbre', arbreId], data);
    },
    onError: (error) => {
      console.error('Error deleting arbre image:', error);
    }
  });
}

/**
 * Hook for rotating arbre images
 * Returns mutation object with rotate functionality
 */
export function useArbreImageRotate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ arbreId, field, rotation }: ArbreImageRotateParams): Promise<ArbreResponse> => {
      // Validate rotation value
      if (![0, 90, 180, 270].includes(rotation)) {
        throw new Error('La rotation doit être 0, 90, 180 ou 270 degrés');
      }

      return services.arbre.rotateImage(arbreId, field, rotation);
    },
    onSuccess: (data, { arbreId }) => {
      // Invalidate and refetch relevant queries
      queryClient.invalidateQueries({ queryKey: ['arbres'] });
      queryClient.invalidateQueries({ queryKey: ['arbre', arbreId] });
      queryClient.invalidateQueries({ queryKey: ['inventaire'] });

      // Update the cache directly with the new data
      queryClient.setQueryData(['arbre', arbreId], data);
    },
    onError: (error) => {
      console.error('Error rotating arbre image:', error);
    }
  });
}

/**
 * Combined hook that provides upload, delete, and rotate functionality
 * Useful when you need all operations in a single component
 */
export function useArbreImageOperations() {
  const uploadMutation = useArbreImageUpload();
  const deleteMutation = useArbreImageDelete();
  const rotateMutation = useArbreImageRotate();

  return {
    upload: uploadMutation,
    delete: deleteMutation,
    rotate: rotateMutation,
    isLoading: uploadMutation.isPending || deleteMutation.isPending || rotateMutation.isPending,
    isError: uploadMutation.isError || deleteMutation.isError || rotateMutation.isError,
    error: uploadMutation.error || deleteMutation.error || rotateMutation.error
  };
}

/**
 * Hook specifically designed for ImageUpload component integration
 * Handles the conversion from base64/URL to File upload
 */
export function useImageUploadForArbre(arbreId: number, field: 'img1' | 'img2' | 'img3') {
  const uploadMutation = useArbreImageUpload();
  const deleteMutation = useArbreImageDelete();
  const rotateMutation = useArbreImageRotate();

  const uploadImage = async (file: File) => {
    return uploadMutation.mutateAsync({ arbreId, field, file });
  };

  const deleteImage = async () => {
    return deleteMutation.mutateAsync({ arbreId, field });
  };

  const rotateImage = async (rotation: number) => {
    return rotateMutation.mutateAsync({ arbreId, field, rotation });
  };

  const handleImageChange = async (fieldName: string, value: string | File) => {
    try {
      if (value instanceof File) {
        // Direct file upload
        await uploadImage(value);
      } else if (typeof value === 'string' && value === '') {
        // Empty string means delete
        await deleteImage();
      }
      // For string URLs, we assume they're already uploaded
    } catch (error) {
      console.error(`Error handling image change for ${field}:`, error);
      throw error;
    }
  };

  return {
    uploadImage,
    deleteImage,
    rotateImage,
    handleImageChange,
    isUploading: uploadMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isRotating: rotateMutation.isPending,
    isLoading: uploadMutation.isPending || deleteMutation.isPending || rotateMutation.isPending,
    uploadError: uploadMutation.error,
    deleteError: deleteMutation.error,
    rotateError: rotateMutation.error,
    error: uploadMutation.error || deleteMutation.error || rotateMutation.error
  };
}
